'use client';

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useLiteMode } from '@/hooks/useLiteMode.hook';
import { StackRevealProvider, useStackReveal } from './stats-stack-reveal';

type Props = {
  className?: string;
  children: ReactNode;
};

// 모바일 스택 축소 기준(stats.css .ty-stats-fan). 179 = 상단 여백 50 + 헤드라인 89 + 간격 40, 20 = 아래 여백,
// 630 = 스택 전체 높이(마지막 카드 top 60 + 카드 570)
const FAN_RESERVED_PX = 199;
const FAN_HEIGHT_PX = 630;

/**
 * 섹션 루트(<section>). 연출은 stats.css 가 구동하고, 여기서는 모션 스위치 속성만 켠다.
 * data-inview: 처음 들어올 때 1회(데스크탑 진입) / data-playing: 화면 안이고 라이트 모드가 아닐 때(상시 루프) /
 * data-motion-fallback: 모바일 스택을 motion 이 대신 구동할 때(stats-stack-reveal.tsx)
 */
export function StatsMotion({ className, children }: Props) {
  const ref = useRef<HTMLElement>(null);
  const { inView, playing } = useSectionSwitches(ref);
  useFitFanToStage(ref);
  const stackReveal = useStackReveal(ref);

  return (
    <StackRevealProvider value={stackReveal}>
      <section
        ref={ref}
        className={className}
        data-inview={inView || undefined}
        data-playing={playing || undefined}
        data-motion-fallback={stackReveal.mode || undefined}
      >
        {children}
      </section>
    </StackRevealProvider>
  );
}

/** inView: 처음 들어온 뒤로 계속 true / playing: 지금 화면 안이고 라이트 모드가 아님 */
function useSectionSwitches(ref: RefObject<HTMLElement | null>) {
  const isLite = useLiteMode();
  const [inView, setInView] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(
    function observeSectionVisibility() {
      const el = ref.current;
      if (!el) return;
      const observer = new IntersectionObserver(
        ([observed]) => {
          setVisible(observed.isIntersecting);
          if (observed.isIntersecting) setInView(true);
        },
        { rootMargin: '0px 0px -20% 0px' },
      );
      observer.observe(el);
      return function disconnectObserver() {
        observer.disconnect();
      };
    },
    [ref],
  );

  return { inView, playing: visible && !isLite };
}

// CSS 만으로(tan(atan2(100svh …))) 계산하면 iOS Safari 가 음수를 내 스택이 위로 뒤집힌다 — 실제 무대 높이를 재서 넘긴다
function useFitFanToStage(ref: RefObject<HTMLElement | null>) {
  useEffect(
    function fitFanToStage() {
      const section = ref.current;
      const stage = section?.querySelector<HTMLElement>('.ty-stats-stage');
      if (!section || !stage) return;
      const observer = new ResizeObserver(() => {
        const ratio = (stage.clientHeight - FAN_RESERVED_PX) / FAN_HEIGHT_PX;
        section.style.setProperty(
          '--ty-fan-scale',
          String(Math.min(1, Math.max(0.1, ratio))),
        );
      });
      observer.observe(stage);
      return function disconnectFanObserver() {
        observer.disconnect();
      };
    },
    [ref],
  );
}
